/**
 * Copyright (c) 2026, CheckPlus Limited
 */

export const HEADER_VARIANTS = [
  {
    id: "classic",
    label: "Classic",
    description: "Logo on the left, org info on the right"
  },
  {
    id: "centered",
    label: "Centered",
    description: "Logo and org info centered"
  },
  {
    id: "minimal",
    label: "Minimal",
    description: "Org name only, no logo background"
  }
];

export const TABLE_VARIANTS = [
  {
    id: "standard",
    label: "Standard",
    description: "Two-column table layout"
  },
  {
    id: "bordered",
    label: "Bordered",
    description: "Table with row dividers"
  },
  {
    id: "striped",
    label: "Striped",
    description: "Alternating row backgrounds"
  }
];

export const TABLE_ROWS = [
  { key: "receiptNumber", label: "Receipt Number", defaultEn: "Receipt Number", defaultZh: "收據編號" },
  { key: "transactionType", label: "Transaction Type", defaultEn: "Transaction Type", defaultZh: "款項性質" },
  { key: "transactionDate", label: "Transaction Date", defaultEn: "Transaction Date", defaultZh: "付款日期 (YYYY/MM/DD)" },
  { key: "paymentMethod", label: "Payment Method", defaultEn: "Payment Method", defaultZh: "付款方法" },
  { key: "item", label: "Item", defaultEn: "Item", defaultZh: "項目" },
  { key: "amount", label: "Amount", defaultEn: "Amount", defaultZh: "金額" },
  { key: "coverFee", label: "Cover Fee", defaultEn: "Cover Fee", defaultZh: "手續費覆蓋" },
  { key: "donor", label: "Donor / Payer", defaultEn: "Donor", defaultZh: "捐款者", hasOrganizationVariant: true, defaultEnOrganization: "Organization", defaultZhOrganization: "捐款機構" },
  { key: "donorContactPerson", label: "Contact Person", defaultEn: "Contact Person", defaultZh: "聯絡人" },
  { key: "email", label: "Email Address", defaultEn: "Email Address", defaultZh: "電郵地址" },
  { key: "phone", label: "Phone Number", defaultEn: "Phone Number", defaultZh: "電話號碼" },
  { key: "note", label: "Note", defaultEn: "Note", defaultZh: "備註" }
];

export const DEFAULT_VISIBLE_ROWS = {
  receiptNumber: true,
  transactionType: true,
  transactionDate: true,
  paymentMethod: true,
  item: true,
  amount: true,
  coverFee: true,
  donor: true,
  donorContactPerson: true,
  email: true,
  phone: true,
  note: true,
  customFields: true
};

/**
 * Ensure a saved template has every fixed element this version of the editor
 * expects. Templates saved before a new fixed element was introduced (e.g. the
 * `reissued` element added in CP-130) won't have it on disk; injecting it on
 * load lets existing orgs see and configure the new control without a DB
 * migration. Inserts each missing fixed element at its default position from
 * `getDefaultReceiptTemplate()`.
 */
export function ensureRequiredElements(template) {
  if (!template || !Array.isArray(template.elements)) return template;
  const defaults = getDefaultReceiptTemplate().elements;
  const presentTags = new Set(
    template.elements
      .filter((e) => e.type === "fixed" && e.tag)
      .map((e) => e.tag)
  );
  const missing = defaults.filter(
    (d) => d.type === "fixed" && d.tag && !presentTags.has(d.tag)
  );
  if (missing.length === 0) return template;
  return { ...template, elements: [...template.elements, ...missing] };
}

export function getDefaultReceiptTemplate() {
  return {
    version: 1,
    elements: [
      {
        id: "header",
        type: "fixed",
        tag: "header",
        x: 0,
        y: 0,
        width: 500,
        height: 100,
        visible: true,
        variant: "classic"
      },
      {
        id: "heading",
        type: "fixed",
        tag: "heading",
        x: 50,
        y: 110,
        width: 400,
        height: 30,
        visible: true
      },
      {
        id: "reissued",
        type: "fixed",
        tag: "reissued",
        x: 50,
        y: 141,
        width: 400,
        height: 8,
        visible: true,
        customText: "Re-issuance #N 補發",
        fontSize: 6
      },
      {
        id: "table",
        type: "fixed",
        tag: "table",
        x: 50,
        y: 150,
        width: 400,
        height: 380,
        visible: true,
        variant: "standard",
        labelWidth: 20,
        visibleRows: { ...DEFAULT_VISIBLE_ROWS }
      },
      {
        id: "footer",
        type: "fixed",
        tag: "footer",
        x: 50,
        y: 550,
        width: 400,
        height: 100,
        visible: true,
        additionalContent: ""
      }
    ]
  };
}
